import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { db } from "@/lib/firebase";
import { addDoc, collection, Timestamp } from "firebase/firestore";
import { getNextDocumentNumber } from "@/lib/documentNumber";
import { printDocument } from "@/lib/printHelper";
import InvoiceForm from "@/components/forms/InvoiceForm";
import InvoicePreview from "@/components/preview/InvoicePreview";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Printer, Save, RotateCcw, Eye, Edit, Loader2 } from "lucide-react";

// GST rate for EV three-wheelers: 5% total (2.5% CGST + 2.5% SGST)
const GST_RATE = 0.05;

const getInitialFormData = (invoiceNumber = "") => ({
  invoiceNumber,
  invoiceDate: new Date().toISOString().split("T")[0],
  deliveryDate: "",
  customerName: "",
  customerAddress: "",
  customerPhone: "",
  customerAadhaar: "",
  customerPan: "",
  customerGstin: "",
  vehicleModel: "",
  vehicleVariant: "",
  vehicleColor: "",
  chassisNumber: "",
  motorNumber: "",
  batteryMake: "",
  batteryAh: "",
  batteryNumber: "",
  batteryIncluded: "Yes — With Battery",
  chargerDetails: "",
  exShowroomPrice: "",
  // cgst & sgst are computed automatically — NOT stored as user-editable fields
  rtoCharges: "",
  insuranceCharges: "",
  insuranceCompany: "",
  accessoriesCharges: "",
  otherCharges: "",
  discount: "",
  paymentMode: "",
  paymentReference: "",
  financierName: "",
  financierNameCustom: "",
  notes: "",
});

const InvoicePage = () => {
  const [formData, setFormData] = useState(getInitialFormData("Loading..."));
  const [activeTab, setActiveTab] = useState("form");
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [numberLoading, setNumberLoading] = useState(true);
  const printRef = useRef();

  // Load the next invoice number from Firestore on mount
  useEffect(() => {
    const loadNumber = async () => {
      setNumberLoading(true);
      const num = await getNextDocumentNumber("INV", "invoices", "invoiceNumber");
      setFormData(getInitialFormData(num));
      setNumberLoading(false);
    };
    loadNumber();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "batteryIncluded" && value === "No — Without Battery") {
        updated.batteryNumber = "N/A";
      } else if (name === "batteryIncluded" && value === "Yes — With Battery") {
        if (updated.batteryNumber === "N/A") updated.batteryNumber = "";
      }
      return updated;
    });
  };

  const handleReset = async () => {
    setNumberLoading(true);
    const num = await getNextDocumentNumber("INV", "invoices", "invoiceNumber");
    setFormData(getInitialFormData(num));
    setNumberLoading(false);
    setActiveTab("form");
    toast.success("Form reset successfully");
  };

  // Auto-computed GST values (2.5% each on ex-showroom price)
  const computedCgst = () => {
    const price = parseFloat(formData.exShowroomPrice) || 0;
    return Math.round(price * (GST_RATE / 2));
  };
  const computedSgst = () => computedCgst();

  const calculateSubtotal = () => {
    return (
      (parseFloat(formData.exShowroomPrice) || 0) +
      (parseFloat(formData.rtoCharges) || 0) +
      (parseFloat(formData.insuranceCharges) || 0) +
      (parseFloat(formData.accessoriesCharges) || 0) +
      (parseFloat(formData.otherCharges) || 0)
    );
  };

  const calculateTax = () => computedCgst() + computedSgst();

  const calculateTotal = () =>
    calculateSubtotal() + calculateTax() - (parseFloat(formData.discount) || 0);

  // Required fields validation
  const validate = () => {
    const required = [
      { key: "customerName", label: "Customer Name" },
      { key: "customerPhone", label: "Phone Number" },
      { key: "customerAddress", label: "Customer Address" },
      { key: "customerPan", label: "PAN Card Number" },
      { key: "vehicleModel", label: "Vehicle Model" },
      { key: "vehicleColor", label: "Vehicle Color" },
      { key: "chassisNumber", label: "Chassis Number" },
      { key: "motorNumber", label: "Motor Number" },
      { key: "exShowroomPrice", label: "Ex-Showroom Price" },
      { key: "paymentMode", label: "Payment Mode" },
    ];
    for (const f of required) {
      if (!formData[f.key] || formData[f.key].toString().trim() === "") {
        toast.error(`Please fill in: ${f.label}`);
        setActiveTab("form");
        return false;
      }
    }
    if (!parseFloat(formData.exShowroomPrice) || parseFloat(formData.exShowroomPrice) <= 0) {
      toast.error("Ex-Showroom Price must be greater than 0");
      setActiveTab("form");
      return false;
    }
    return true;
  };

  // Save to Firestore
  const saveToDb = async () => {
    const cgst = computedCgst();
    const sgst = computedSgst();
    const invoiceRef = await addDoc(collection(db, "invoices"), {
      ...formData,
      cgst: String(cgst),
      sgst: String(sgst),
      total: calculateTotal(),
      savedAt: Timestamp.now(),
    });



    return invoiceRef;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      await saveToDb();
      toast.success(`Invoice ${formData.invoiceNumber} saved successfully!`);
      const nextNum = await getNextDocumentNumber("INV", "invoices", "invoiceNumber");
      setFormData(getInitialFormData(nextNum));
      setActiveTab("form");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save invoice. Check Firebase configuration.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!validate()) return;

    // 1. Switch to preview FIRST so the document is rendered before print is called
    setActiveTab("preview");
    setIsPrinting(true);

    // 2. Wait for React to render the preview tab
    await new Promise((resolve) => setTimeout(resolve, 500));

    // 3. Open the browser print dialog via printHelper (clones to body level for reliable A4)
    await printDocument();

    // 4. After dialog closes: save, toast, reset
    try {
      await saveToDb();
      toast.success(`Invoice ${formData.invoiceNumber} saved & printed!`);
      const nextNum = await getNextDocumentNumber("INV", "invoices", "invoiceNumber");
      setFormData(getInitialFormData(nextNum));
      setActiveTab("form");
    } catch (error) {
      console.error(error);
      toast.error("Printed but failed to save. Check Firebase.");
    } finally {
      setIsPrinting(false);
    }
  };

  // Enrich formData with computed GST for passing to form / preview
  const enrichedFormData = {
    ...formData,
    cgst: String(computedCgst()),
    sgst: String(computedSgst()),
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Action Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 no-print">
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground">
            Tax Invoice / Bill
          </h2>
          <p className="text-sm text-muted-foreground">
            Create and print invoice for vehicle sale
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} className="gap-2">
            <RotateCcw className="h-4 w-4" /> Reset
          </Button>
          <Button variant="secondary" onClick={handleSave} className="gap-2" disabled={isSaving || numberLoading}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button onClick={handlePrint} className="gap-2 bg-primary text-primary-foreground" disabled={isPrinting || numberLoading}>
            {isPrinting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            {isPrinting ? "Saving & Printing..." : "Print"}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 no-print">
          <TabsTrigger value="form" className="gap-2">
            <Edit className="h-4 w-4" /> Edit Form
          </TabsTrigger>
          <TabsTrigger value="preview" className="gap-2">
            <Eye className="h-4 w-4" /> Preview
          </TabsTrigger>
        </TabsList>

        <TabsContent value="form" className="mt-4">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-heading">Invoice Details</CardTitle>
            </CardHeader>
            <CardContent>
              <InvoiceForm
                formData={enrichedFormData}
                onInputChange={handleInputChange}
                onSelectChange={handleSelectChange}
                calculateTotal={calculateTotal}
                computedCgst={computedCgst()}
                computedSgst={computedSgst()}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="mt-4">
          <div className="no-print mb-3 p-3 bg-secondary/30 rounded-lg border border-border text-sm text-muted-foreground">
            Click <strong>Print</strong> to send to printer. The preview below shows the exact A4 output.
          </div>
          <InvoicePreview
            ref={printRef}
            formData={enrichedFormData}
            subtotal={calculateSubtotal()}
            tax={calculateTax()}
            total={calculateTotal()}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InvoicePage;
